import { useState } from 'react';
import { Ban, CalendarCheck, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { PAGES } from '../../constants/pages';

import {
    StockTransferState,
    useAbortStockTransfersMutation,
    useValidateStockTransfersMutation,
    TotadminPermission,
    useStockTransfersUpdatedSubscription,
} from 'data/__generated__';

import { ActionBar } from 'components/ActionBar';
import { Header, HeaderTitle } from 'components/Header';
import { PageTitle } from 'components/PageTitle';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { confirmPopup } from 'components/Popups/Confirm';
import { StockTransfersList } from './components/StockTransfersList';
import type { StockTransferRow } from './constants/stockTransfersColumns';

import { handleUpdateActionEndToast } from 'pages/SupplyOrders/shared/handleUpdateActionEndToast';
import { ValidateStockTransfersAtDatePopup } from './popups/ValidateStockTransfersAtDatePopup';
import { sortStockTransfers } from './utils/sortStockTransfers';
import { stockTransfersUpdatedHandler } from './utils/stockTransfersUpdatedHandler';

export const StockTransfers = () => {
    const [isValidateStockTransfersAtDatePopupOpen, setIsValidateStockTransfersAtDatePopupOpen] =
        useState<boolean>(false);

    const [selectedStockTransferIds, setSelectedStockTransferIds] = useState<string[]>([]);
    const [stockTransfers, setStockTransfers] = useState<StockTransferRow[]>([]);

    useStockTransfersUpdatedSubscription({
        onData: ({ data: { data } }) => {
            if (data) {
                stockTransfersUpdatedHandler(data.stockTransferUpdated);
            }
        },
    });

    const [validateStockTransfers, { loading: validateStockTransfersLoading }] = useValidateStockTransfersMutation();
    const [abortStockTransfers, { loading: abortStockTransfersLoading }] = useAbortStockTransfersMutation();

    const displayedStockTransfers = sortStockTransfers(stockTransfers);

    const selectedStockTransfers = displayedStockTransfers.filter(({ _id }) => selectedStockTransferIds.includes(_id));

    const actionSections = [
        {
            title: 'Actions',
            actions: [
                {
                    icon: <CalendarCheck />,
                    label: 'Valider les STs à une date de transfert donnée',
                    requiredPermissions: [TotadminPermission.StockTransfersManage],
                    onClick: () => {
                        setIsValidateStockTransfersAtDatePopupOpen(true);
                    },
                    loading: abortStockTransfersLoading || validateStockTransfersLoading,
                },
            ],
        },
        {
            title: 'Marquer comme',
            actions: [
                {
                    icon: <Check />,
                    label: 'Validé(s)',
                    requiredPermissions: [TotadminPermission.StockTransfersManage],
                    onClick: async () => {
                        const { data } = await validateStockTransfers({
                            variables: { stockTransferIds: selectedStockTransfers.map(({ _id }) => _id) },
                        });

                        if (!data) {
                            throw new Error('Une erreur est survenue lors de la validation des transferts de stock');
                        }

                        const {
                            validateStockTransfers: { errors, stockTransfers },
                        } = data;

                        handleUpdateActionEndToast({
                            displaySuccess: !!stockTransfers.length,
                            successText: `${stockTransfers.length} transfert(s) sur ${selectedStockTransfers.length} a/ont été validé(s)`,
                            errors,
                            warnings: [],
                        });
                    },
                    hidden:
                        !selectedStockTransfers.length ||
                        selectedStockTransfers.some(({ state }) => state !== StockTransferState.Created),
                    loading: abortStockTransfersLoading || validateStockTransfersLoading,
                },
                {
                    icon: <Ban />,
                    label: 'Annulé(s)',
                    requiredPermissions: [TotadminPermission.StockTransfersManage],
                    onClick: async () => {
                        const hasValidatedReassortment = selectedStockTransfers.some(
                            ({ state, reassortmentFlow }) =>
                                !!reassortmentFlow && state === StockTransferState.Validated,
                        );
                        const hasConfirmed = await confirmPopup({
                            title: 'Confirmer',
                            message: hasValidatedReassortment
                                ? 'Un réassort validé est sélectionné : annuler va aussi annuler les box associées et reverser les stocks préparés. Êtes-vous sûr.e ?'
                                : 'Êtes-vous sûr.e de vouloir annuler le(s) transfert(s) de stock ?',
                        });
                        if (!hasConfirmed) return;

                        const { data } = await abortStockTransfers({
                            variables: { stockTransferIds: selectedStockTransfers.map(({ _id }) => _id) },
                        });

                        if (!data) {
                            throw new Error("Une erreur est survenue lors de l'annulation des transferts de stock");
                        }

                        const {
                            abortStockTransfers: { errors, stockTransfers },
                        } = data;

                        handleUpdateActionEndToast({
                            displaySuccess: !!stockTransfers.length,
                            successText: `${stockTransfers.length} transfert(s) sur ${selectedStockTransfers.length} a/ont été annulé(s)`,
                            errors,
                            warnings: [],
                        });
                    },
                    hidden:
                        !selectedStockTransfers.length ||
                        selectedStockTransfers.some(({ state }) => state === StockTransferState.Aborted),
                    loading: abortStockTransfersLoading || validateStockTransfersLoading,
                },
            ],
        },
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.stockTransfers} />
                </HeaderTitle>
                <ButtonsContainer>
                    <Link to="/stock-transfer/returns">
                        <TotemPrimaryButton>Consulter retours entrepôt</TotemPrimaryButton>
                    </Link>
                    <Link to="/stock-transfer/create">
                        <TotemPrimaryButton requiredPermissions={[TotadminPermission.StockTransfersManage]}>
                            Créer un transfert de stock
                        </TotemPrimaryButton>
                    </Link>
                </ButtonsContainer>
            </Header>
            <ContentContainer>
                <Content>
                    <ListContainer>
                        <StockTransfersList
                            selectedKeys={selectedStockTransferIds}
                            onSelectionChange={setSelectedStockTransferIds}
                            onStockTransfersChange={setStockTransfers}
                        />
                    </ListContainer>
                </Content>
                <ActionBar actionSections={actionSections} isInitiallyOpen={!!selectedStockTransfers.length} />
            </ContentContainer>
            <ValidateStockTransfersAtDatePopup
                isOpen={isValidateStockTransfersAtDatePopupOpen}
                setIsOpen={setIsValidateStockTransfersAtDatePopupOpen}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const ContentContainer = styled.div`
    display: flex;
    flex: 1;
    overflow: hidden;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    padding: 15px;
    flex: 1;
    overflow: hidden;
`;

const ListContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;
